import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import 'swiper/scss';

import { Capacitor } from '@capacitor/core';
import { IonButton, IonContent, IonImg } from '@ionic/react';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperType } from 'swiper/types';

import arrow from '@/assets/imgs/onboarding/arrow.svg';
import background1 from '@/assets/imgs/onboarding/bg1.webp';
import background1Large from '@/assets/imgs/onboarding/bg1-lg.webp';
import item1 from '@/assets/imgs/onboarding/item1.webp';
import item1Large from '@/assets/imgs/onboarding/item1-lg.webp';
import item2 from '@/assets/imgs/onboarding/item2.webp';
import item2Large from '@/assets/imgs/onboarding/item2-lg.webp';
import item3 from '@/assets/imgs/onboarding/item3.webp';
import item4 from '@/assets/imgs/onboarding/item4.webp';
import item5 from '@/assets/imgs/onboarding/item5.webp';
import item5Large from '@/assets/imgs/onboarding/item5-lg.webp';
import item6Large from '@/assets/imgs/onboarding/item6-lg.webp';
import phone1 from '@/assets/imgs/onboarding/phone1.webp';
import phone2 from '@/assets/imgs/onboarding/phone2.webp';
import phone3 from '@/assets/imgs/onboarding/phone3.webp';
import phone4 from '@/assets/imgs/onboarding/phone4.webp';
import useMediaQuery, { Breakpoint } from '@/hooks/useMediaQuery';

import { ROUTES_PATH } from '../../constants';
import { storageService } from '../../services/storage';
import styles from './OnBoarding.module.scss';

export default function OnBoarding() {
  const swiperRef = useRef<SwiperType | null>(null);
  const isTablet = useMediaQuery(Breakpoint.Mobile);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [swiperActive, setSwiperActive] = useState(0);

  useEffect(() => {
    const isFirstTime = storageService.getOnboardingStorage();
    if (isFirstTime === 'false' || !Capacitor.isNativePlatform()) {
      window.location.href = ROUTES_PATH.LOGIN;
      storageService.setOnboardingSeen();
    }
  }, []);

  const handleFinishOnBoarding = () => {
    storageService.setOnboardingSeen();
    window.location.href = ROUTES_PATH.LOGIN;
  };

  const handleSkip = () => {
    const currentIdx = swiperRef.current?.activeIndex;
    if (currentIdx !== undefined && currentIdx < 3) {
      swiperRef.current?.slideTo(currentIdx + 1);
    }
  };

  const renderSkipButton = () => (
    <IonButton
      fill="clear"
      className={styles.skipButton}
      onClick={() => handleFinishOnBoarding()}
    >
      Saltar
    </IonButton>
  );

  return (
    <IonContent
      className={classNames(
        styles.onboardingContainer,
        isTablet && styles.tablet,
      )}
    >
      {!isLastSlide && renderSkipButton()}
      <Swiper
        onSwiper={(swiper: any) => {
          swiperRef.current = swiper;
        }}
        className={isLastSlide ? classNames(styles.lastSwiperContainer, styles.lastSlide) : styles.swiperContainer}
        pagination={{
          bulletActiveClass: 'swiper-active-bullet',
          bulletClass: 'swiper-bullet',
          clickable: true,
        }}
        autoplay={{
          delay: 4000,
          stopOnLastSlide: true,
        }}
        modules={[Pagination, Autoplay]}
        onPaginationUpdate={(swiperCore: any) => {
          setSwiperActive(swiperCore.activeIndex);
          if (swiperCore.activeIndex === 3) {
            swiperCore.pagination.destroy();
            setIsLastSlide(true);
          } else {
            swiperCore.pagination.init();
            setIsLastSlide(false);
          }
        }}
      >
        <SwiperSlide>
          <IonContent fullscreen>
            <div className={styles.slide}>
              <div className={styles.topSection}>
                {isTablet ? (
                  <>
                    <IonImg src={item1Large} className={styles.itemBgTablet1} />
                    <IonImg src={item6Large} className={styles.itemBgTablet2} />
                  </>
                ) : (
                  <IonImg src={item1} className={styles.itemBg} />
                )}
                <IonImg src={phone1} className={styles.phone} />
              </div>
              <div className={styles.bottomSection}>
                {isTablet ? (
                  <IonImg
                    src={background1Large}
                    className={styles.background}
                  />
                ) : (
                  <IonImg src={background1} className={styles.background} />
                )}
                <div className={styles.textWrapper}>
                  <p className={styles.onboardTitle}>Operaciones</p>
                  <p className={styles.onboardSubtitle}>
                    Consulta todas tus operaciones, retoma las que están
                    pendientes y crea nuevas de forma sencilla.
                  </p>
                </div>
              </div>
            </div>
          </IonContent>
        </SwiperSlide>
        <SwiperSlide>
          <IonContent fullscreen>
            <div className={styles.slide}>
              <div className={styles.topSection}>
                {isTablet ? (
                  <IonImg src={item2Large} className={styles.item2BgTablet} />
                ) : (
                  <IonImg src={item2} className={styles.itemBg} />
                )}
                <IonImg src={phone2} className={styles.phone} />
              </div>
              <div className={styles.bottomSection}>
                <div className={styles.textWrapper}>
                  <p className={styles.onboardTitle}>Operación Express</p>
                  <p className={styles.onboardSubtitle}>
                    El proceso mas rápido y sencillo para financiaciones
                    express.
                  </p>
                </div>
              </div>
            </div>
          </IonContent>
        </SwiperSlide>
        <SwiperSlide>
          <IonContent fullscreen>
            <div className={styles.slide}>
              <div className={styles.topSection}>
                {isTablet ? (
                  <>
                    <IonImg src={item1Large} className={styles.itemBgTablet1} />
                    <IonImg src={item6Large} className={styles.itemBgTablet2} />
                  </>
                ) : (
                  <>
                    <IonImg src={item3} className={styles.itemBgRight} />
                    <IonImg src={item4} className={styles.itemBg} />
                  </>
                )}
                <IonImg src={phone3} className={styles.phone} />
              </div>
              <div className={styles.bottomSection}>
                <div className={styles.textWrapper}>
                  <p className={styles.onboardTitle}>Reporting</p>
                  <p className={styles.onboardSubtitle}>
                    Visualiza y compara los datos y tendencias de tus
                    operaciones en Pepper.
                  </p>
                </div>
              </div>
            </div>
          </IonContent>
        </SwiperSlide>
        <SwiperSlide>
          <IonContent fullscreen>
            <div className={styles.slide}>
              <div className={styles.topSection}>
                {isTablet ? (
                  <>
                    <IonImg
                      src={item5Large}
                      className={styles.item5BgTablet1}
                    />
                    <IonImg
                      src={item5Large}
                      className={styles.item5BgTablet2}
                    />
                  </>
                ) : (
                  <IonImg src={item5} className={styles.itemBg} />
                )}
                <IonImg src={phone4} className={styles.phone} />
              </div>
              <div className={classNames(styles.bottomSection, styles.lastSlide)}>
                <div className={classNames(styles.textWrapper, styles.lastSlide)}>
                  <p className={styles.onboardTitle}>Formación</p>
                  <p className={styles.onboardSubtitle}>
                    Entra en la sección para mantenerte siempre al día con los
                    últimos artículos y webinars.
                  </p>
                </div>
                <IonButton
                  onClick={() => handleFinishOnBoarding()}
                  expand="block"
                  className={styles.finishButton}
                >
                  ¡Comenzamos!
                </IonButton>
              </div>
            </div>
          </IonContent>
        </SwiperSlide>
      </Swiper>
      {!isLastSlide && (
        <div id="pagination-container" className={styles.paginationContainer}>
          <span className={styles.whiteElement} />
          <div className={styles.paginationBulletsContainer}>
            <span
              className={classNames(
                styles.paginationBullet,
                swiperActive === 0 && styles.isActive,
              )}
            />
            <span
              className={classNames(
                styles.paginationBullet,
                swiperActive === 1 && styles.isActive,
              )}
            />
            <span
              className={classNames(
                styles.paginationBullet,
                swiperActive === 2 && styles.isActive,
              )}
            />
          </div>
          <IonButton
            className={styles.arrowButton}
            fill="clear"
            onClick={() => handleSkip()}
          >
            <img src={arrow} alt="Arrow Icon" className={styles.arrowIcon} />
          </IonButton>
        </div>
      )}
    </IonContent>
  );
}
